import React, { useState, useRef, useEffect } from "react";
import { Sidebar } from "../components/sidebar";
import { ChatInput } from "../components/chat-input";
import { AddVehicleDialog } from "../components/add-vehicle-dialog";
import { PreviewRateLimitModal } from "../components/preview-rate-limit-modal";
import { ChatSessionLayout } from "../components/chat-session-layout";
import { useMobile } from "../hooks/use-mobile";
import { apiService } from "../services/apiService";
import { vehicleService, VEHICLE_SEPARATOR } from "../services/vehicleService";
import { Chat, Message } from "../types/chat";
import { Vehicle } from "../types/vehicle";
import { useSearchParams } from 'react-router-dom';
import { useVehicle } from "../contexts/VehicleContext";
import { SourcesManager, Source } from "../lib/sources-utils";

const PreviewChatPage = () => {
  const [chat, setChat] = useState<Chat | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [input, setInput] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(true); // Start with dialog open for first vehicle selection
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamedMessage, setStreamedMessage] = useState("");
  const [hasProcessedQuery, setHasProcessedQuery] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [highlightedSourceId, setHighlightedSourceId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const { vehicles } = useVehicle(); // Use VehicleContext to get vehicles added through dialog

  const isMobile = useMobile();

  // Initialize sources manager
  const sourcesManagerRef = useRef(new SourcesManager());
  const [currentSources, setCurrentSources] = useState<Source[]>([]);

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
      setShowSources(false);
    }
  }, [isMobile]);

  // Listen for vehicles added through AddVehicleDialog
  useEffect(() => {
    if (vehicles.length > 0 && !selectedVehicle) {
      const latestVehicle = vehicles[vehicles.length - 1]; // Get the most recently added vehicle
      handleVehicleAdded(latestVehicle);
    }
  }, [vehicles, selectedVehicle]);

  // Handle URL query parameters for initial message and vehicle selection
  useEffect(() => {
    const queryParam = searchParams.get('query');
    const makeParam = searchParams.get('make');
    const modelParam = searchParams.get('model');
    const typeParam = searchParams.get('type');
    
    // If vehicle params are provided, create vehicle automatically
    if (makeParam && modelParam && typeParam && !selectedVehicle) {
      const createVehicleFromParams = async () => {
        try {
          const vehicleId = `${makeParam}${VEHICLE_SEPARATOR}${modelParam}${VEHICLE_SEPARATOR}${typeParam}`;
          const vehicleName = await vehicleService.getVehicleNameById(vehicleId);
          
          const vehicle: Vehicle = {
            id: vehicleId,
            name: vehicleName,
            make: makeParam,
            model: modelParam,
            type: typeParam,
          };
          
          handleVehicleAdded(vehicle);
        } catch (error) {
          console.error('Error creating vehicle from URL parameters:', error);
          // If we can't create the vehicle from params, fall back to showing the dialog
        }
      };
      
      createVehicleFromParams();
      return;
    }
    
    // Handle query parameter for initial message
    if (queryParam && !hasProcessedQuery && selectedVehicle && chat) {
      setHasProcessedQuery(true);
      // Automatically submit the query as the first message
      const submitQuery = async () => {
        const event = { preventDefault: () => {} } as React.FormEvent<HTMLFormElement>;
        setInput(queryParam);
        // Small delay to ensure input is set
        setTimeout(() => {
          handleSubmit(event, queryParam);
        }, 100);
      };
      submitQuery();
    }
  }, [searchParams, selectedVehicle, chat, hasProcessedQuery]);

  const createNewChat = async (vehicle: Vehicle): Promise<Chat> => {
    try {
      // For preview mode, we don't use authentication token
      const chatId = await apiService.startSession(vehicle, null);
      const chat: Chat = {
        id: chatId,
        title: 'Preview Chat',
        messages: [],
        userId: 'preview',
        vehicleId: vehicle.id,
        public: false,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      return chat;
    } catch (error) {
      throw error;
    }
  };

  const handleVehicleAdded = async (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowAddVehicle(false);
    
    try {
      const newChat = await createNewChat(vehicle);
      setChat(newChat);
    } catch (error) {
      console.error("Error creating preview chat:", error);
      if (apiService.isRateLimitError(error)) {
        setShowRateLimitModal(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, messageOverride?: string) => {
    e.preventDefault();
    const messageContent = messageOverride || input;
    if (!messageContent.trim() || !chat || isLoading) return;

    setIsLoading(true);
    setStreamedMessage("");

    try {
      const newMessage: Message = {
        role: "user",
        content: messageContent,
        timestamp: Date.now(),
      };

      // Update chat with user message immediately
      const updatedChat = { ...chat, messages: [...chat.messages, newMessage] };
      setChat(updatedChat);
      setInput("");

      // Don't create assistant message placeholder yet - wait for streaming to start
      let assistantContent = "";

      // Send message to API (without auth token for preview)
      await apiService.sendMessage(chat.id, messageContent, (chunk) => {
        assistantContent += chunk;
        
        // Parse sources from accumulated content
        const parsed = sourcesManagerRef.current.parseAndReplaceLinks(assistantContent);
        
        // Update sources if new ones were found
        if (parsed.sources.length > 0) {
          setCurrentSources(sourcesManagerRef.current.getAllSources());
        }
        
        // Set the streamed message with parsed content
        setStreamedMessage(parsed.text);
        setIsLoading(false); // Hide loading spinner as soon as we start receiving chunks
      }, null);

      // After streaming is complete, add the full assistant message to chat history
      const assistantMessage: Message = {
        role: "assistant",
        content: assistantContent,
        timestamp: Date.now(),
      };

      const finalChat = { ...updatedChat, messages: [...updatedChat.messages, assistantMessage] };
      
      // Update title if this is the first exchange
      if (finalChat.messages.length === 2) {
        // Generate a simple title from the first user message
        const words = messageContent.trim().split(' ').slice(0, 4);
        let title = words.join(' ');
        if (messageContent.split(' ').length > 4) {
          title += '...';
        }
        finalChat.title = title || 'Preview Chat';
      }

      finalChat.updatedAt = Date.now();
      setChat(finalChat);
      setStreamedMessage(""); // Clear streamed message now that it's in the chat history
    } catch (error) {
      console.error("Error sending message:", error);
      
      if (apiService.isRateLimitError(error)) {
        setShowRateLimitModal(true);
      } else {
        console.error("Unexpected error sending message:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeVehicle = () => {
    setSelectedVehicle(null);
    setChat(null);
    setShowAddVehicle(true);
    setStreamedMessage("");
    setInput("");
    setHasProcessedQuery(false);
    // Clear sources when changing vehicle
    sourcesManagerRef.current.clearSources();
    setCurrentSources([]);
  };

  const banner = selectedVehicle ? (
    <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-emerald-400 text-sm font-medium">Preview Mode</p>
          <p className="text-emerald-300 text-xs">{selectedVehicle?.name}</p>
        </div>
        <button
          onClick={handleChangeVehicle}
          className="text-emerald-400 hover:text-emerald-300 text-xs underline"
        >
          Change Vehicle
        </button>
      </div>
    </div>
  ) : null;

  const footer = (
    <ChatInput
      input={input}
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
    />
  );

  return (
    <div className="flex bg-gradient-to-br from-zinc-900 to-black text-white overflow-hidden mobile-viewport safe-area-bottom">
      <Sidebar
        chats={[]}
        currentChatId=""
        onSelectChat={() => {}}
        onCreateNewChat={() => {}}
        onDeleteChat={() => {}}
        onRenameChat={() => {}}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        onAddVehicle={() => setShowAddVehicle(true)}
        isPreviewMode={true}
        previewVehicleName={selectedVehicle?.name}
      />
      <ChatSessionLayout
        chatName={chat?.title || 'Preview Chat'}
        vehicleName={selectedVehicle?.name || ''}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        showSources={showSources}
        onToggleSources={() => setShowSources(!showSources)}
        sources={currentSources}
        highlightedSourceId={highlightedSourceId}
        messages={chat?.messages || []}
        streamedMessage={streamedMessage}
        isLoading={isLoading}
        messagesEndRef={messagesEndRef}
        banner={banner}
        footer={footer}
        chatId={chat?.id}
        onSourceClick={(source) => {
          setHighlightedSourceId(source.id);
          setShowSources(true);
          setTimeout(() => setHighlightedSourceId(undefined), 3000);
        }}
      />
      <AddVehicleDialog open={showAddVehicle} onOpenChange={setShowAddVehicle} />
      <PreviewRateLimitModal open={showRateLimitModal} onOpenChange={setShowRateLimitModal} />
    </div>
  );
};

export default PreviewChatPage;
